// User-customisable keybindings for the in-game board UI.
//
// Each action has a stable `id`, a human-readable `label`, a grouping
// `section`, and a `defaultBinding`. User overrides are persisted
// through persistentPrefs so they survive port changes and app updates.
//
// Bindings are compared via `matchAction(event)` which returns the
// first action id whose binding matches the keydown event. The match
// uses `code` when the default binding specifies one (Digit1–Digit9,
// Space, Tab, etc.) so modifier + digit shortcuts work on non-US
// keyboard layouts; otherwise it matches on a case-insensitive `key`.

import { getPref, setPref } from '../persistentPrefs';
import { k } from '../../localization/t';

const STORAGE_KEY = 'vk-keybindings';

// Standard-mapping button indices (Xbox labels, matches the W3C Gamepad
// spec). Exported so the gamepad dispatcher can refer to them by name.
export const GAMEPAD_BUTTON = {
  A: 0, B: 1, X: 2, Y: 3,
  LB: 4, RB: 5, LT: 6, RT: 7,
  BACK: 8, START: 9,
  L3: 10, R3: 11,
  DPAD_UP: 12, DPAD_DOWN: 13, DPAD_LEFT: 14, DPAD_RIGHT: 15,
};

// Canonical action catalogue. `defaultBinding` is the authoritative
// source of truth for the built-in keyboard shortcut; `defaultGamepad`
// (optional) is the controller default for Phase 1. Actions with no
// keyboard binding (grab, cancel) carry `defaultBinding: null`. They
// are controller-native and only reachable with a pad.
export const KEYBINDING_ACTIONS = [
  // Cards. Act on the hovered card or the current marquee selection.
  { id: 'card.flip', section: k('Cards'), label: k('Flip face-down / face-up'), defaultBinding: { key: 'f' }, defaultGamepad: { button: GAMEPAD_BUTTON.Y } },
  { id: 'card.tap', section: k('Cards'), label: k('Tap / untap'), defaultBinding: { key: 't' }, defaultGamepad: { button: GAMEPAD_BUTTON.X } },
  { id: 'card.toHand', section: k('Cards'), label: k('Send to hand'), defaultBinding: { key: 'h' }, defaultGamepad: { button: GAMEPAD_BUTTON.LB } },
  { id: 'card.inspect', section: k('Cards'), label: k('Inspect in full size'), defaultBinding: { code: 'Space' }, defaultGamepad: { button: GAMEPAD_BUTTON.LT, hold: true } },
  { id: 'card.group', section: k('Cards'), label: k('Group / ungroup selection'), defaultBinding: { key: 'g' } },
  { id: 'card.toCemetery', section: k('Cards'), label: k('Send hovered card to cemetery / delete hovered die'), defaultBinding: { key: 'Backspace' }, defaultGamepad: { button: GAMEPAD_BUTTON.RB } },

  // Controller-native. No keyboard equivalent. These drive the
  // pickup-and-place flow that replaces mouse drag.
  { id: 'grab', section: k('Cards'), label: k('Pick up / place card'), defaultBinding: null, defaultGamepad: { button: GAMEPAD_BUTTON.A } },
  { id: 'cancel', section: k('Cards'), label: k('Cancel / back'), defaultBinding: null, defaultGamepad: { button: GAMEPAD_BUTTON.B } },

  // Camera.
  { id: 'camera.panUp', section: k('Camera'), label: k('Pan view up'), defaultBinding: { key: 'w' } },
  { id: 'camera.panLeft', section: k('Camera'), label: k('Pan view left'), defaultBinding: { key: 'a' } },
  { id: 'camera.panDown', section: k('Camera'), label: k('Pan view down'), defaultBinding: { key: 's' } },
  { id: 'camera.panRight', section: k('Camera'), label: k('Pan view right'), defaultBinding: { key: 'd' } },
  { id: 'camera.zoomOverview', section: k('Camera'), label: k('Zoom to overview'), defaultBinding: { code: 'Digit1', shift: true }, defaultGamepad: { button: GAMEPAD_BUTTON.L3 } },
  { id: 'camera.zoomCard', section: k('Camera'), label: k('Zoom to hovered card'), defaultBinding: { code: 'Digit2', shift: true }, defaultGamepad: { button: GAMEPAD_BUTTON.R3 } },
  { id: 'camera.flipPerspective', section: k('Camera'), label: k('Flip perspective (P1/P2)'), defaultBinding: { code: 'Digit3', shift: true } },
  { id: 'view.surface',    section: k('Camera'), label: k('Show surface view'),    defaultBinding: { key: 'w', shift: true } },
  { id: 'view.subsurface', section: k('Camera'), label: k('Show subsurface view'), defaultBinding: { key: 's', shift: true } },

  // Piles.
  { id: 'pile.searchSpellbook', section: k('Piles'), label: k('Open Spellbook'), defaultBinding: { key: 'x' } },
  { id: 'pile.searchAtlas', section: k('Piles'), label: k('Open Atlas'), defaultBinding: { key: 'z' } },
  { id: 'pile.searchCemetery', section: k('Piles'), label: k('Open Cemetery'), defaultBinding: { key: 'c' } },
  { id: 'pile.searchCollection', section: k('Piles'), label: k('Open Collection'), defaultBinding: { key: 'v' } },
  { id: 'pile.searchBanished', section: k('Piles'), label: k('Open Banished'), defaultBinding: { key: 'n' } },
  { id: 'pile.shuffle', section: k('Piles'), label: k('Shuffle hovered pile / reroll hovered die'), defaultBinding: { key: 'r' } },

  // Game.
  { id: 'game.passTurn', section: k('Game'), label: k('Pass the turn'), defaultBinding: { code: 'Tab' }, defaultGamepad: { button: GAMEPAD_BUTTON.START } },
  { id: 'game.tokenBrowser', section: k('Game'), label: k('Open Token Browser'), defaultBinding: { key: 'b' } },
  { id: 'game.lifeUp', section: k('Game'), label: k('Increase life'), defaultBinding: { code: 'ArrowUp' }, defaultGamepad: { button: GAMEPAD_BUTTON.DPAD_UP } },
  { id: 'game.lifeDown', section: k('Game'), label: k('Decrease life'), defaultBinding: { code: 'ArrowDown' }, defaultGamepad: { button: GAMEPAD_BUTTON.DPAD_DOWN } },
  { id: 'game.manaUp', section: k('Game'), label: k('Increase mana'), defaultBinding: { code: 'ArrowRight' }, defaultGamepad: { button: GAMEPAD_BUTTON.DPAD_RIGHT } },
  { id: 'game.manaDown', section: k('Game'), label: k('Decrease mana'), defaultBinding: { code: 'ArrowLeft' }, defaultGamepad: { button: GAMEPAD_BUTTON.DPAD_LEFT } },
  { id: 'game.focusChat', section: k('Game'), label: k('Focus chat input'), defaultBinding: { code: 'Enter' }, defaultGamepad: { button: GAMEPAD_BUTTON.BACK } },
  { id: 'game.showHelp', section: k('Game'), label: k('Show keyboard shortcuts'), defaultBinding: { key: '?' } },
  { id: 'ping.fire', section: k('Game'), label: k('Hold to ping (click to fire)'), defaultBinding: { key: 'q' } },
  { id: 'board.expandSquare', section: k('Game'), label: k('Hold to select a square (click it to fan out its cards)'), defaultBinding: { key: 'e' } },
];

// Ordered list of radial-menu slices, 12 o'clock clockwise. Exposed
// here so the radial component and the settings-help overlay render
// the same thing.
export const RADIAL_ACTIONS = [
  'card.group',
  'pile.shuffle',
  'camera.flipPerspective',
  'game.tokenBrowser',
  'pile.searchSpellbook',
  'pile.searchAtlas',
  'pile.searchCemetery',
  'pile.searchCollection',
  'pile.searchBanished',
];

// Map for O(1) lookups.
const ACTIONS_BY_ID = new Map(KEYBINDING_ACTIONS.map((a) => [a.id, a]));

function loadOverrides() {
  const stored = getPref(STORAGE_KEY, null);
  if (stored && typeof stored === 'object') return { ...stored };
  return {};
}

let overrides = null;
function ensureLoaded() {
  if (overrides === null) overrides = loadOverrides();
  return overrides;
}

function normalizeBinding(binding) {
  if (!binding || typeof binding !== 'object') return null;
  const out = {};
  if (typeof binding.key === 'string' && binding.key) out.key = binding.key;
  if (typeof binding.code === 'string' && binding.code) out.code = binding.code;
  if (binding.shift) out.shift = true;
  if (binding.ctrl) out.ctrl = true;
  if (binding.alt) out.alt = true;
  if (binding.meta) out.meta = true;
  if (!out.key && !out.code) return null;
  return out;
}

export function getBinding(actionId) {
  const action = ACTIONS_BY_ID.get(actionId);
  if (!action) return null;
  const o = ensureLoaded();
  if (Object.prototype.hasOwnProperty.call(o, actionId)) {
    // null / undefined override means "unbound"
    return o[actionId] ? normalizeBinding(o[actionId]) : null;
  }
  return normalizeBinding(action.defaultBinding);
}

export function getAllBindings() {
  const result = {};
  for (const action of KEYBINDING_ACTIONS) {
    result[action.id] = getBinding(action.id);
  }
  return result;
}

export function setBinding(actionId, binding) {
  if (!ACTIONS_BY_ID.has(actionId)) return;
  const o = ensureLoaded();
  o[actionId] = binding ? normalizeBinding(binding) : null;
  setPref(STORAGE_KEY, o);
}

export function clearBinding(actionId) {
  setBinding(actionId, null);
}

export function resetBinding(actionId) {
  if (!ACTIONS_BY_ID.has(actionId)) return;
  const o = ensureLoaded();
  delete o[actionId];
  setPref(STORAGE_KEY, o);
}

export function resetAllBindings() {
  overrides = {};
  setPref(STORAGE_KEY, overrides);
}

// Build a binding object from a raw KeyboardEvent. For letter / symbol
// keys we store `key` (layout-independent semantic character); for the
// physical-position keys we care about (digits, arrows, Tab, Space,
// Enter, Backspace) we store `code` so it survives non-US layouts.
const CODE_ONLY_KEYS = new Set([
  'Digit0', 'Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6', 'Digit7', 'Digit8', 'Digit9',
  'Numpad0', 'Numpad1', 'Numpad2', 'Numpad3', 'Numpad4', 'Numpad5', 'Numpad6', 'Numpad7', 'Numpad8', 'Numpad9',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'Tab', 'Space', 'Enter', 'Backspace', 'Escape',
  'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
]);

// For a single-character `key` that isn't a letter (digits, punctuation,
// symbols), the produced character already encodes whether Shift was
// held. E.g. `?` is `Shift+/` on US, `Shift+ß` on German, `Shift+'` on
// Spanish, but `event.key` is `?` in every case. Storing/comparing an
// extra Shift modifier would make the binding match only the layout it
// was recorded on, so we treat Shift as intrinsic for these keys.
function isSymbolKey(key) {
  return typeof key === 'string' && key.length === 1 && key.toLowerCase() === key.toUpperCase();
}

export function bindingFromEvent(event) {
  const out = {};
  if (CODE_ONLY_KEYS.has(event.code)) {
    out.code = event.code;
  } else if (event.key && event.key.length === 1) {
    out.key = event.key.toLowerCase();
  } else if (event.key) {
    out.key = event.key;
  } else {
    return null;
  }
  const symbolKey = !out.code && isSymbolKey(out.key);
  if (event.shiftKey && !symbolKey) out.shift = true;
  if (event.ctrlKey) out.ctrl = true;
  if (event.altKey) out.alt = true;
  if (event.metaKey) out.meta = true;
  return out;
}

function bindingMatchesEvent(binding, event) {
  if (!binding) return false;
  const symbolKey = !binding.code && isSymbolKey(binding.key);
  if (!symbolKey && Boolean(binding.shift) !== event.shiftKey) return false;
  if (Boolean(binding.ctrl) !== event.ctrlKey) return false;
  if (Boolean(binding.alt) !== event.altKey) return false;
  if (Boolean(binding.meta) !== event.metaKey) return false;
  if (binding.code) return event.code === binding.code;
  if (binding.key) {
    if (!event.key) return false;
    if (binding.key.length === 1) return event.key.toLowerCase() === binding.key.toLowerCase();
    return event.key === binding.key;
  }
  return false;
}

// Return the first action whose binding matches this event, or null.
// Bindings are checked in the order KEYBINDING_ACTIONS defines; if two
// actions share the same binding the earlier one wins (the settings UI
// prevents this, but we stay deterministic anyway).
export function matchAction(event) {
  for (const action of KEYBINDING_ACTIONS) {
    const binding = getBinding(action.id);
    if (bindingMatchesEvent(binding, event)) return action.id;
  }
  return null;
}

// Pretty label for a binding, suitable for rendering inside a <Kbd>.
const KEY_LABEL_OVERRIDES = {
  ArrowUp: '\u2191',
  ArrowDown: '\u2193',
  ArrowLeft: '\u2190',
  ArrowRight: '\u2192',
  Space: 'Space',
  Tab: 'Tab',
  Enter: 'Enter',
  Backspace: 'Backspace',
  Escape: 'Esc',
};

export function describeBinding(binding) {
  if (!binding) return 'Unbound';
  const parts = [];
  const symbolKey = !binding.code && isSymbolKey(binding.key);
  if (binding.meta) parts.push(typeof navigator !== 'undefined' && /Mac/.test(navigator.platform) ? '\u2318' : 'Meta');
  if (binding.ctrl) parts.push('Ctrl');
  if (binding.alt) parts.push(typeof navigator !== 'undefined' && /Mac/.test(navigator.platform) ? '\u2325' : 'Alt');
  if (binding.shift && !symbolKey) parts.push('Shift');
  if (binding.code) {
    if (binding.code.startsWith('Digit')) parts.push(binding.code.slice(5));
    else if (binding.code.startsWith('Numpad')) parts.push(`Num${binding.code.slice(6)}`);
    else if (binding.code.startsWith('F') && /^F\d+$/.test(binding.code)) parts.push(binding.code);
    else parts.push(KEY_LABEL_OVERRIDES[binding.code] || binding.code);
  } else if (binding.key) {
    parts.push(binding.key.length === 1 ? binding.key.toUpperCase() : (KEY_LABEL_OVERRIDES[binding.key] || binding.key));
  }
  return parts.join('+');
}

export function findActionByBinding(binding, { exclude } = {}) {
  if (!binding) return null;
  for (const action of KEYBINDING_ACTIONS) {
    if (exclude && action.id === exclude) continue;
    const existing = getBinding(action.id);
    if (!existing) continue;
    const symbolKey = !existing.code && !binding.code && isSymbolKey(existing.key) && isSymbolKey(binding.key);
    if (
      (symbolKey || Boolean(existing.shift) === Boolean(binding.shift)) &&
      Boolean(existing.ctrl) === Boolean(binding.ctrl) &&
      Boolean(existing.alt) === Boolean(binding.alt) &&
      Boolean(existing.meta) === Boolean(binding.meta) &&
      (existing.code ? existing.code === binding.code : (existing.key || '').toLowerCase() === (binding.key || '').toLowerCase())
    ) {
      return action.id;
    }
  }
  return null;
}
